import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type AnimationEvent,
  type CSSProperties,
  type KeyboardEvent,
  type TransitionEvent,
} from "react";
import { Buton, Iskelet } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import { carkCevir, carkDurumu } from "./api-oyun2";
import {
  LAMBALAR,
  duzenGetir,
  hedefAciHesapla,
  konfetiUret,
  okAltindaki,
  puanVarMi,
  type CarkDuzeni,
  type KonfetiParcasi,
} from "./cark-geometri";
import { CARK_BASLIK_FONTU, CARK_CSS_DEGISKENLERI, CARK_TEMA } from "./cark-tema";
import { ikiKareSonra } from "./cark-kare";
import { hataMetni } from "./hata-metni";
import { CARK_HESAP_DURUMU, type CarkDurumu, type CarkSonucu } from "@koclukpanel/contracts";
import { useUsteKaydir } from "./SiparisDetay";
import "./cark.css";

/**
 * ÇARK — F-GAM-19, oyunlaştırma dalga 2 / sürüm 7b (öğrenci tarafı). Görünüm: "Klasik TV" Çarkıfelek çizimi
 * (`koclukpaneli-araclar/oyunlastirma/cark-klasik-tv.dc.html`, Emre 07.10.2026) BİREBİR.
 *
 * SONUCU SUNUCU BELİRLER (dalga-2-tasarim.md §10 "ÇARK TASARIMI" + "ÇARK ORANLARI"): hak, sonraki hak metni, bakiye,
 * puan kümesi ve SONUÇ sunucudan gelir. Ekran ağırlık/olasılık BİLMEZ ve GÖSTERMEZ; yalnız sunucunun döndürdüğü
 * puanı taşıyan dilimlerden birinin %30–%70'inde durur (`cark-geometri.ts`).
 *
 * ÇOCUK GÜVENLİĞİ (bağlayıcı; F-GAM-01, dalga-2-tasarim.md §3.3) — bunların her biri bilerek YOK:
 *   · ek çevirme, puanla ya da parayla hak alma; hak birikmez (sunucu kuralı);
 *   · canlı geri sayım, saniye sayacı, `setInterval`, bildirim izni isteme;
 *   · "az kaldı"/"kıl payı": ok dilim kenarına yanaşmaz, komşu dilimde yavaşlamaz; dönüş eğrisi taşmaz.
 * Tek zamanlayıcı dönüşün bitişi için GÜVENLİK yedeğidir (`transitionend` gelmezse 5,3 sn); geri sayım değildir.
 *
 * `prefers-reduced-motion`: dönüş, ampul, dil, nabız ve konfeti animasyonu yok; sonuç ve kutlama kartı doğrudan.
 */

/** Tasarım: dönüş 5,2 sn; sonuç 5,3 sn'de (`setTimeout(…, 5300)`). Burada yalnız `transitionend`in yedeği. */
const GUVENLIK_MS = 5300;
/** Dönüş iki kare (≈33 ms) geç başlar; güvenlik yedeği de o kadar geç düşer. */
const KARE_PAYI_MS = 100;
/** Kutlamada kare başına eklenen konfeti parçası (110 parça ≈ 4 parti). */
const KONFETI_PARTISI = 30;

/**
 * Çevirme istek kimliği: yanıtı kaybolan bir isteği yeniden denemek AYNI sonucu döndürür, ikinci çevirme olmaz.
 * `randomUUID` yalnız güvenli bağlamda (https) var; yerel ağdaki geliştirme adresinde yedeği devreye girer
 * (`Magaza.tsx` ile aynı kalıp).
 */
function yeniIstekKimligi(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function azHareketMi(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

function puanMetni(n: number): string {
  return n.toLocaleString("tr-TR");
}

/** Sunucunun puan kümesi (+ varsa kümede olmayan sonuç puanı): çark yalnız bunları gösterir. */
function puanlari(durum: CarkDurumu | null, ek: number | null): number[] {
  const p = (durum?.dilimler ?? []).map((d) => d.puan);
  if (ek !== null && !p.includes(ek)) p.push(ek);
  return p;
}

/** Tasarım: ≥1000 "İnanılmaz! Büyük ödül!", ≥50 "Süper şans!", diğerleri "Tebrikler!". */
function kutlamaBasligi(puan: number): string {
  return puan >= 1000 ? "İnanılmaz! Büyük ödül!" : puan >= 50 ? "Süper şans!" : "Tebrikler!";
}

type Asama = "bos" | "istek" | "donuyor";

interface Kutlama {
  sonuc: CarkSonucu;
  /** `null`: hareket azaltma açık — konfeti hiç çizilmez. */
  konfeti: KonfetiParcasi[] | null;
}

export function Cark() {
  const kok = useUsteKaydir();
  const [durum, setDurum] = useState<CarkDurumu | null>(null);
  const [kapali, setKapali] = useState(false);
  const [yuklemeHatasi, setYuklemeHatasi] = useState<string | null>(null);
  const [yenile, setYenile] = useState(0);
  const [asama, setAsama] = useState<Asama>("bos");
  const [hata, setHata] = useState<string | null>(null);
  /** Bu ekranda tamamlanan çevirme (sunucu tazelemesi `sonCevirme`yi getirmezse yedek). */
  const [sonuc, setSonuc] = useState<CarkSonucu | null>(null);
  /** Sunucunun puan kümesinde olmayan bir sonuç gelirse çark o puanı da içeren düzene geçer. */
  const [ekPuan, setEkPuan] = useState<number | null>(null);
  const [donus, setDonus] = useState(0);
  const [anlik, setAnlik] = useState(false);
  const [kutlama, setKutlama] = useState<Kutlama | null>(null);
  /**
   * Çevirme anında gelen 409 `CARK_HESAP_DURUMU` mesajı (sunucudan; ekran kendi cümlesini üretmez). Durum yeniden
   * yüklenip `cevrilemez` gelince sunucudaki alan kullanılır; yükleme başarısızsa bu mesaj kalır.
   */
  const [hesapMesaji, setHesapMesaji] = useState<string | null>(null);

  // İstek kimliği ekran açılınca BİR KEZ üretilir; hata sonrası yeniden denemede AYNI kalır, yalnız başarılı
  // sonuçtan ya da hak yok yanıtından sonra yenilenir.
  const istekKimligi = useRef<string | null>(null);
  if (istekKimligi.current === null) istekKimligi.current = yeniIstekKimligi();
  /** Çift tıklama koruması: durum güncellemesi çizilmeden gelen ikinci tıklama da geri çevrilir. */
  const islemde = useRef(false);
  const acik = useRef(true);
  const donusRef = useRef(0);
  const bekleyen = useRef<CarkSonucu | null>(null);
  const zamanlayici = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Bekleyen "iki kare sonra" işi (dönüşü başlatan); bitişte/ayrılışta iptal edilir. */
  const iptalKare = useRef<(() => void) | null>(null);
  const sonucKarti = useRef<HTMLDivElement | null>(null);
  const odakSonuca = useRef(false);

  const duzen = duzenGetir(puanlari(durum, ekPuan));

  useEffect(() => {
    acik.current = true;
    return () => {
      acik.current = false;
      if (zamanlayici.current !== null) clearTimeout(zamanlayici.current);
      iptalKare.current?.();
    };
  }, []);

  /**
   * Gösterilen sonuç çarkın gösterdiği dilimle ÇELİŞMESİN: açılışta (ya da 409 sonrası) `sonCevirme` gösterilecekse
   * çark animasyonsuz o puanın bir diliminin üstüne getirilir. Ok zaten o puandaysa dokunulmaz.
   */
  const konumla = useCallback((d: CarkDurumu) => {
    if (d.hakVar || !d.sonCevirme) return;
    const puan = d.sonCevirme.puan;
    const dz = duzenGetir(puanlari(d, puan));
    if (!puanVarMi(duzenGetir(puanlari(d, null)), puan)) setEkPuan(puan);
    if (okAltindaki(donusRef.current, dz).puan === puan) return;
    const { aci } = hedefAciHesapla(donusRef.current, dz, puan, Math.random, 0);
    donusRef.current = aci;
    setAnlik(true);
    setDonus(aci);
  }, []);

  useEffect(() => {
    let iptal = false;
    void (async () => {
      try {
        const d = await carkDurumu();
        if (iptal) return;
        setYuklemeHatasi(null);
        if (d.acik === false) {
          setKapali(true);
          return;
        }
        konumla(d);
        setDurum(d);
      } catch (e) {
        if (iptal) return;
        if (e instanceof ApiHatasi && e.durum === 404) setKapali(true);
        else setYuklemeHatasi(hataMetni(e, "Çark alınamadı."));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [yenile, konumla]);

  /** Sunucudan güncel hak/bakiye/`sonCevirme`; başarısızsa ekrandaki yerel durum kalır. */
  const sessizTazele = useCallback(
    async (konumlansin: boolean) => {
      try {
        const d = await carkDurumu();
        if (!acik.current) return;
        if (d.acik === false) {
          setKapali(true);
          return;
        }
        if (konumlansin) konumla(d);
        setHesapMesaji(null);
        setDurum(d);
      } catch {
        /* Tazeleme başarısızsa yerel durum (hak yok) doğrudur; ekranı hataya düşürmeye gerek yok. */
      }
    },
    [konumla],
  );

  /** Dönüş bitti (ya da hareket azaltma): sonuç, bakiye ve kutlama BURADA görünür — öncesinde değil. */
  const bitir = useCallback(() => {
    const s = bekleyen.current;
    if (!s) return;
    bekleyen.current = null;
    iptalKare.current?.();
    iptalKare.current = null;
    if (zamanlayici.current !== null) {
      clearTimeout(zamanlayici.current);
      zamanlayici.current = null;
    }
    if (!acik.current) return;
    islemde.current = false;
    setAsama("bos");
    setSonuc(s);
    setDurum((d) => (d ? { ...d, hakVar: false, bakiye: s.yeniBakiye, sonCevirme: s } : d));
    setKutlama({ sonuc: s, konfeti: azHareketMi() ? null : konfetiUret(s.puan, Math.random) });
    // Sonraki hak metni sunucudan; çark zaten sonucun üstünde, yeniden konumlanmaz.
    void sessizTazele(false);
  }, [sessizTazele]);

  async function cevir() {
    if (islemde.current || !durum || !durum.hakVar) return;
    islemde.current = true;
    setAsama("istek");
    setHata(null);
    let s: CarkSonucu;
    try {
      s = await carkCevir({ istekKimligi: istekKimligi.current! });
    } catch (e) {
      if (!acik.current) return;
      islemde.current = false;
      setAsama("bos");
      if (e instanceof ApiHatasi && e.durum === 409 && e.kod === CARK_HESAP_DURUMU) {
        // Hesap durumu çevirme anında değişti: çevirme OLMADI (yeni kimlik zararsız); sunucunun mesajı gösterilir.
        istekKimligi.current = yeniIstekKimligi();
        setHesapMesaji(e.message);
        void sessizTazele(false);
      } else if (e instanceof ApiHatasi && e.durum === 409 && e.kod === "HAK_YOK") {
        // Hak yok yanıtı çevirmenin OLMADIĞI anlamına gelir; yeni kimlik zararsız.
        istekKimligi.current = yeniIstekKimligi();
        setHata("Şu an çevirme hakkın yok.");
        // `sonCevirme` ve `sonrakiHak` yeniden yüklemeyle gelir; çark o sonucun dilimine geçer.
        void sessizTazele(true);
      } else {
        setHata(hataMetni(e, "Çark çevrilemedi. Tekrar dene."));
      }
      return;
    }
    if (!acik.current) return;
    istekKimligi.current = yeniIstekKimligi();

    // Sonuç puanı çarkta yoksa (sunucu tablosu değişti) çark onu da içeren düzene geçer: gösterilen dilim ile
    // kazanılan puan HİÇBİR koşulda çelişmez.
    const mevcut = duzenGetir(puanlari(durum, null));
    if (!puanVarMi(mevcut, s.puan)) setEkPuan(s.puan);
    const dz = duzenGetir(puanlari(durum, s.puan));
    const { aci } = hedefAciHesapla(donusRef.current, dz, s.puan, Math.random);
    donusRef.current = aci;
    bekleyen.current = s;

    if (azHareketMi()) {
      setAnlik(true);
      setDonus(aci);
      bitir();
      return;
    }
    // İlk karede ampul/dil durumu çizilir, dönüş BİR SONRAKİ karede başlar (hıçkırık giderme, `cark-kare.ts`).
    setAnlik(false);
    setAsama("donuyor");
    iptalKare.current = ikiKareSonra(() => {
      iptalKare.current = null;
      if (acik.current && bekleyen.current === s) setDonus(aci);
    });
    zamanlayici.current = setTimeout(bitir, GUVENLIK_MS + KARE_PAYI_MS);
  }

  const donusBitti = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName && e.propertyName !== "transform") return;
      bitir();
    },
    [bitir],
  );

  const kutlamayiKapat = useCallback(() => {
    odakSonuca.current = true;
    setKutlama(null);
  }, []);

  useEffect(() => {
    if (kutlama === null && odakSonuca.current) {
      odakSonuca.current = false;
      sonucKarti.current?.focus();
    }
  }, [kutlama]);

  if (kapali) return <p className="text-notr-600 text-sm">Çark şu an kapalı.</p>;
  if (yuklemeHatasi && durum === null) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 w-full px-3 py-2 text-sm">
          {yuklemeHatasi}
        </p>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            setYuklemeHatasi(null);
            setYenile((n) => n + 1);
          }}
        >
          Tekrar dene
        </Buton>
      </div>
    );
  }
  if (durum === null) return <Iskelet satir={5} />;

  const donuyor = asama !== "bos";
  // Hesap durumu çevirmeye izin vermiyorsa (dondurulmuş vb.) sunucunun mesajı; yoksa null.
  const cevrilemezMesaji = durum.cevrilemez?.mesaj ?? hesapMesaji;
  // Hak varken eski çevirmenin kartı düğmeyi gizlemez: kart yalnız hak yokken (bugünün çevirmesi) gösterilir.
  // Çevrilemez durumda kart yerine kapalı düğme + mesaj çizilir: öğrenci neden çeviremediğini görmeli.
  const gosterilen =
    !durum.hakVar && !donuyor && cevrilemezMesaji === null ? (durum.sonCevirme ?? sonuc) : null;
  const lambaModu = kutlama ? "kutla" : asama === "donuyor" ? "hizli" : "";
  const nabizAktif = durum.hakVar && !donuyor && gosterilen === null && cevrilemezMesaji === null;
  const sonrakiHakId = "cark-sonraki-hak";
  const hesapMesajiId = "cark-hesap-mesaji";

  return (
    <div
      ref={kok}
      data-testid="cark-sahne"
      className="cark-sahne -mx-4 -mb-4"
      style={CARK_CSS_DEGISKENLERI}
    >
      <div className="cark-ic">
        <div className="cark-ust">
          <h3 className="cark-baslik">Çarkıfelek</h3>
          <div className="cark-bakiye">
            <span className="cark-bakiye-etiket">Bakiye</span>
            <span className="cark-bakiye-deger">{puanMetni(durum.bakiye)} puan</span>
          </div>
        </div>

        <CarkGorseli
          duzen={duzen}
          donus={donus}
          anlik={anlik}
          lambaModu={lambaModu}
          dilSalla={asama === "donuyor"}
          onDonusBitti={donusBitti}
        />

        {gosterilen ? (
          <div ref={sonucKarti} tabIndex={-1} role="status" className="cark-sonuc">
            <span className="cark-sonuc-puan">+{puanMetni(gosterilen.puan)}</span>
            <span className="cark-sonuc-metin">
              puan kazandın.
              {durum.sonrakiHak && (
                <>
                  <br />
                  Yeni hakkın {durum.sonrakiHak}.
                </>
              )}
            </span>
          </div>
        ) : (
          <CevirDugmesi
            metin={donuyor ? "Çark dönüyor…" : "ÇEVİR"}
            kapali={!durum.hakVar || cevrilemezMesaji !== null}
            donuyor={donuyor}
            nabiz={nabizAktif}
            aciklamaId={
              cevrilemezMesaji !== null ? hesapMesajiId : !durum.hakVar ? sonrakiHakId : undefined
            }
            onCevir={() => void cevir()}
          />
        )}

        {cevrilemezMesaji !== null && !donuyor && (
          <p id={hesapMesajiId} role="status" className="cark-bilgi">
            {cevrilemezMesaji}
          </p>
        )}

        {!durum.hakVar && !gosterilen && !donuyor && cevrilemezMesaji === null && (
          <p id={sonrakiHakId} className="cark-bilgi">
            {durum.sonrakiHak ? `Bir sonraki hakkın: ${durum.sonrakiHak}` : "Her gün bir hak."}
          </p>
        )}

        {hata && (
          <p role="alert" className="cark-hata">
            {hata}
          </p>
        )}

        <span className="cark-not">Günde bir dönüş · kazandığın puan anında bakiyende</span>
      </div>

      {kutlama && <KutlamaKatmani kutlama={kutlama} onKapat={kutlamayiKapat} />}
    </div>
  );
}

/**
 * ÇEVİR düğmesi + nabız katmanı. Nabız ölçeği 16px'lik yayılmanın düğmeye oranı: genişlik değişirse (320'lik
 * ekran) `ResizeObserver` günceller; yoksa CSS'teki 390 değeri kalır.
 */
function CevirDugmesi({
  metin,
  kapali,
  donuyor,
  nabiz,
  aciklamaId,
  onCevir,
}: {
  metin: string;
  kapali: boolean;
  donuyor: boolean;
  nabiz: boolean;
  aciklamaId: string | undefined;
  onCevir: () => void;
}) {
  const kap = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = kap.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const olc = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        el.style.setProperty("--cark-nabiz-x", String((width + 32) / width));
        el.style.setProperty("--cark-nabiz-y", String((height + 32) / height));
      }
    };
    olc();
    const g = new ResizeObserver(olc);
    g.observe(el);
    return () => g.disconnect();
  }, []);

  return (
    <div ref={kap} className="cark-dugme-kap">
      <span aria-hidden="true" className={nabiz ? "cark-nabiz aktif" : "cark-nabiz"} />
      <button
        type="button"
        className="cark-cevir"
        disabled={kapali || donuyor}
        data-kapali={kapali && !donuyor ? "" : undefined}
        aria-busy={donuyor || undefined}
        aria-describedby={aciklamaId}
        onClick={onCevir}
      >
        <span className="cark-cevir-metin">{metin}</span>
      </button>
    </div>
  );
}

/**
 * Çarkın görseli. Kutlama/sonuç/hata durumu değişince YENİDEN ÇİZİLMEZ (`memo`): yalnız dönüş açısı, ampul kipi
 * ve dilin sallanması değişince çizilir — ve o zaman da içindeki SVG'ler ayrı `memo` bileşenler olduğu için
 * yeniden üretilmez; değişen yalnız `style.transform` ve iki sınıf adıdır.
 */
const CarkGorseli = memo(function CarkGorseli({
  duzen,
  donus,
  anlik,
  lambaModu,
  dilSalla,
  onDonusBitti,
}: {
  duzen: CarkDuzeni;
  donus: number;
  anlik: boolean;
  lambaModu: string;
  dilSalla: boolean;
  onDonusBitti: (e: TransitionEvent<HTMLDivElement>) => void;
}) {
  const ozet = useMemo(() => {
    const p = [...new Set(duzen.dizi)].sort((a, b) => a - b).map(puanMetni);
    const son = p.pop();
    return `Çarkıfelek: ${p.length > 0 ? `${p.join(", ")} ve ${son}` : son} puanlık renkli dilimler`;
  }, [duzen]);

  return (
    <div className="cark-alan" role="img" aria-label={ozet}>
      <div className="cark-kutu">
        <div className="cark-halka" aria-hidden="true">
          <HalkaCizimi />
        </div>
        <CerceveCizimi />
        <div
          className={lambaModu ? `cark-lambalar ${lambaModu}` : "cark-lambalar"}
          aria-hidden="true"
        >
          <Lambalar />
        </div>
        <div
          data-testid="cark-don"
          className="cark-don"
          data-anlik={anlik ? "" : undefined}
          style={{ transform: `rotate(${donus}deg) translateZ(0)` }}
          onTransitionEnd={onDonusBitti}
          aria-hidden="true"
        >
          <DilimCizimi duzen={duzen} />
        </div>
        <ParilitiCizimi />
      </div>
      <div className="cark-dil" aria-hidden="true">
        <DilCizimi salla={dilSalla} />
      </div>
    </div>
  );
});

/** Dış halka (r=203) — kendi katmanında, yalnız katman opaklığı canlanır. */
const HalkaCizimi = memo(function HalkaCizimi() {
  return (
    <svg viewBox="-200 -200 400 400" className="cark-katman">
      <circle
        cx="0"
        cy="0"
        r="203"
        fill="none"
        stroke={CARK_TEMA.halka}
        strokeWidth="2"
        opacity="0.6"
      />
    </svg>
  );
});

/** Çerçeve halkaları + 32 ampul duyu: statik. */
const CerceveCizimi = memo(function CerceveCizimi() {
  const t = CARK_TEMA;
  return (
    <svg viewBox="-200 -200 400 400" className="cark-katman" aria-hidden="true">
      <circle cx="0" cy="0" r="199" fill={t.cerceveIc} />
      <circle cx="0" cy="0" r="195" fill={t.cerceve} />
      <circle
        cx="0"
        cy="0"
        r="184"
        fill="none"
        stroke={t.icCizgi}
        strokeWidth="1.5"
        opacity="0.55"
      />
      <circle cx="0" cy="0" r="180" fill={t.cerceveIc} />
      {LAMBALAR.map((l, i) => (
        <circle
          key={i}
          cx={l.x}
          cy={l.y}
          r="5.6"
          fill={t.duy}
          stroke={t.cerceveIc}
          strokeWidth="1.5"
        />
      ))}
    </svg>
  );
});

/**
 * 32 ampul: her biri kendi küçük katmanı (tasarımın r=9 hâlesi %35 + r=5,6 çekirdek), çerçeve koordinatında aynı
 * yerde. Faz `--i` ile CSS'te; kip değişince bu bileşen çizilmez.
 */
const Lambalar = memo(function Lambalar() {
  return (
    <>
      {LAMBALAR.map((l, i) => (
        <span
          key={i}
          className="cark-lamba"
          style={
            {
              "--i": i,
              left: `${(Number(l.x) + 191) / 4}%`,
              top: `${(Number(l.y) + 191) / 4}%`,
            } as CSSProperties
          }
        >
          <svg viewBox="-9 -9 18 18" className="cark-katman">
            <circle cx="0" cy="0" r="9" fill={CARK_TEMA.lambaHale} opacity="0.35" />
            <circle cx="0" cy="0" r="5.6" fill={CARK_TEMA.lambaCekirdek} />
          </svg>
        </span>
      ))}
    </>
  );
});

/** Dönen katmanın içeriği: dilimler, alt alta rakamlar, mandallar, göbek ve yıldız. Düzen değişmedikçe çizilmez. */
const DilimCizimi = memo(function DilimCizimi({ duzen }: { duzen: CarkDuzeni }) {
  const t = CARK_TEMA;
  return (
    <svg viewBox="-200 -200 400 400" className="cark-katman">
      {duzen.dilimler.map((d, i) => (
        <path
          key={`d${i}`}
          data-dilim={d.puan}
          d={d.yol}
          fill={d.renk}
          stroke={t.ayrac}
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
      ))}
      {duzen.etiketler.map((e, i) => (
        <text
          key={`e${i}`}
          data-etiket-dilim={e.dilim}
          x={e.x}
          y={e.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={CARK_BASLIK_FONTU}
          fontSize={e.boy}
          fontWeight="800"
          fill={e.renk}
          stroke={e.kontur}
          strokeWidth="3.2"
          strokeLinejoin="round"
          paintOrder="stroke"
          transform={e.don}
        >
          {e.metin}
        </text>
      ))}
      {duzen.mandallar.map((m, i) => (
        <circle
          key={`m${i}`}
          cx={m.x}
          cy={m.y}
          r="4.2"
          fill={t.mandal}
          stroke={t.cerceveIc}
          strokeWidth="1.4"
        />
      ))}
      <circle cx="0" cy="0" r="176" fill="none" stroke={t.cerceveIc} strokeWidth="3" />
      <circle cx="0" cy="0" r="44" fill={t.cerceve} />
      <circle cx="0" cy="0" r="38" fill={t.gobek} />
      <path
        d="M0 -24 L6.6 -8.2 L23.5 -7.5 L10.3 3.1 L14.7 19.5 L0 10.2 L-14.7 19.5 L-10.3 3.1 L-23.5 -7.5 L-6.6 -8.2 Z"
        fill={t.gobekIc}
      />
    </svg>
  );
});

/** Dönmeyen parıltı (radialGradient) — çarkın üstünde, statik. */
const ParilitiCizimi = memo(function ParilitiCizimi() {
  const id = useId();
  const t = CARK_TEMA;
  return (
    <svg
      viewBox="-200 -200 400 400"
      className="cark-katman"
      style={{ pointerEvents: "none" }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={id} cx="35%" cy="25%" r="70%">
          <stop offset="0%" stopColor={t.parlakBeyaz} stopOpacity="0.28" />
          <stop offset="55%" stopColor={t.parlakBeyaz} stopOpacity="0.04" />
          <stop offset="100%" stopColor={t.parlakSiyah} stopOpacity="0.18" />
        </radialGradient>
      </defs>
      <circle cx="0" cy="0" r="176" fill={`url(#${id})`} />
    </svg>
  );
});

const DilCizimi = memo(function DilCizimi({ salla }: { salla: boolean }) {
  const t = CARK_TEMA;
  return (
    <svg viewBox="0 0 40 58" className={salla ? "salla" : undefined}>
      <path d="M20 56 L5 17 A15 15 0 1 1 35 17 Z" fill={t.dilKenar} />
      <path d="M20 50 L9 17 A11 11 0 1 1 31 17 Z" fill={t.dil} />
      <circle cx="20" cy="15" r="4.5" fill={t.dilKenar} />
      <path
        d="M13 12 A8 8 0 0 1 20 7"
        fill="none"
        stroke={t.dilParlak}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      />
    </svg>
  );
});

/**
 * KUTLAMA — tasarımın perdesi, konfetisi ve kartı. `role="dialog"`: açılınca odak "Harika!"da, Esc kapatır, Tab
 * dışarı kaçmaz (tek denetim). Konfeti parçaları animasyonları bitince DOM'dan kalkar.
 */
function KutlamaKatmani({ kutlama, onKapat }: { kutlama: Kutlama; onKapat: () => void }) {
  const dugme = useRef<HTMLButtonElement | null>(null);
  const baslikId = useId();
  const puanId = useId();
  const biten = useRef(0);
  const [konfetiBitti, setKonfetiBitti] = useState(false);
  const { sonuc, konfeti } = kutlama;
  /** Çizilen konfeti sayısı: parçalar partiler hâlinde, kartın ARDINDAN kare kare eklenir (aşağıya bak). */
  const [gorunen, setGorunen] = useState(0);

  useEffect(() => {
    dugme.current?.focus();
  }, []);

  /*
    KONFETİ PARTİ PARTİ MONTE EDİLİR (dönüşün SON karesindeki takılma, 6× CPU kısmada 130–210 ms). Dönüş bitince tek
    karede 110 parçanın hepsi oluşuyor ve her biri kendi kompozit katmanına yükseliyordu (stil+katman: ≈70 ms). Parçaların
    animasyon gecikmesi 0–0,9 sn olduğundan birkaç kare geç doğmaları GÖRÜNMEZ; ilk kare yalnız perde+kart, sonra her
    iki karede bir parti. Tasarımdaki parça sayısı/biçimi aynı (60/80/110), yalnız doğuş zamanı yayılıyor.
  */
  useEffect(() => {
    if (!konfeti) return;
    let n = 0;
    let iptal: () => void = () => undefined;
    const sonraki = () => {
      n = Math.min(konfeti.length, n + KONFETI_PARTISI);
      setGorunen(n);
      if (n < konfeti.length) iptal = ikiKareSonra(sonraki);
    };
    iptal = ikiKareSonra(sonraki);
    return () => iptal();
  }, [konfeti]);

  function tus(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      onKapat();
    } else if (e.key === "Tab") {
      e.preventDefault();
      dugme.current?.focus();
    }
  }

  function parcaBitti(e: AnimationEvent<HTMLDivElement>) {
    if (!konfeti || !(e.target instanceof HTMLElement) || !e.target.hasAttribute("data-parca"))
      return;
    biten.current += 1;
    if (biten.current >= konfeti.length) setKonfetiBitti(true);
  }

  return (
    <div
      className="cark-perde"
      data-testid="cark-kutlama"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${baslikId} ${puanId}`}
      onKeyDown={tus}
      onAnimationEnd={parcaBitti}
    >
      {konfeti && !konfetiBitti && <KonfetiKatmani parcalar={konfeti} sayi={gorunen} />}
      <div className="cark-kutlama-kart">
        <div className="cark-rozet" aria-hidden="true">
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            stroke={CARK_TEMA.kutlamaYildiz}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8L3.5 9.2l5.9-.9z" />
          </svg>
        </div>
        <span id={baslikId} className="cark-kutlama-baslik">
          {kutlamaBasligi(sonuc.puan)}
        </span>
        <span id={puanId} className="cark-kutlama-puan">
          +{puanMetni(sonuc.puan)}
        </span>
        <span className="cark-kutlama-alt">puan kazandın</span>
        <span className="cark-kutlama-bakiye">
          Puanın bakiyene eklendi. Yeni bakiyen {puanMetni(sonuc.yeniBakiye)} puan.
        </span>
        <button ref={dugme} type="button" className="cark-harika" onClick={onKapat}>
          Harika!
        </button>
      </div>
    </div>
  );
}

const KonfetiKatmani = memo(function KonfetiKatmani({
  parcalar,
  sayi,
}: {
  parcalar: KonfetiParcasi[];
  sayi: number;
}) {
  return (
    <>
      {parcalar.slice(0, sayi).map((c, i) => (
        <span
          key={i}
          aria-hidden="true"
          data-parca=""
          className="cark-parca"
          style={{
            left: c.sol,
            width: c.en,
            height: c.boy,
            background: c.renk,
            borderRadius: c.yuvarlak,
            animationDuration: c.sure,
            animationDelay: c.gecikme,
          }}
        />
      ))}
    </>
  );
});
